import { GlobalWorkerOptions, getDocument } from 'pdfjs-dist'

export const CLAIM_MAX_DOCUMENT_PAGE_COUNT = 50

function resolvePdfjsWorkerSrc() {
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '')
  const fromPublic = `${base}/pdf.worker.min.js`
  try {
    // eslint-disable-next-line global-require
    const workerModule = require('pdfjs-dist/build/pdf.worker.min.js')
    const url = workerModule?.default ?? workerModule
    if (typeof url === 'string' && url.trim()) return url.trim()
  } catch {
    // fall through to the public worker copied by the app scripts
  }
  return fromPublic
}

if (!GlobalWorkerOptions.workerSrc) {
  GlobalWorkerOptions.workerSrc = resolvePdfjsWorkerSrc()
}

function isPdfJsWorkerLoadError(error) {
  const message = String(error?.message || error || '')
  return (
    message.includes('Setting up fake worker failed') ||
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Failed to load module script') ||
    message.includes('Strict MIME type checking') ||
    message.includes('Unexpected token') ||
    message.includes('Content Security Policy') ||
    message.includes('CSP')
  )
}

async function getDocumentWithWorkerFallback(options) {
  let loadingTask = null
  try {
    loadingTask = getDocument(options)
  } catch (error) {
    if (isPdfJsWorkerLoadError(error)) {
      const fallbackTask = getDocument({ ...options, disableWorker: true })
      const pdf = await fallbackTask.promise
      return { loadingTask: fallbackTask, pdf }
    }
    throw error
  }

  try {
    const pdf = await loadingTask.promise
    return { loadingTask, pdf }
  } catch (error) {
    try { loadingTask.destroy?.() } catch { /* ignore */ }
    if (!isPdfJsWorkerLoadError(error)) throw error
    const fallbackTask = getDocument({ ...options, disableWorker: true })
    const pdf = await fallbackTask.promise
    return { loadingTask: fallbackTask, pdf }
  }
}

function isPdfFile(file) {
  const type = String(file?.type || '').toLowerCase()
  const name = String(file?.name || '').toLowerCase()
  return type === 'application/pdf' || name.endsWith('.pdf')
}

export function claimDocumentPageCount(document) {
  const raw = document?.pageCount ?? document?.page_count
  const count = Number(raw)
  return Number.isFinite(count) && count > 0 ? Math.max(1, Math.floor(count)) : 1
}

export function claimDocumentsPageCount(documents) {
  return Array.isArray(documents)
    ? documents.reduce((sum, document) => sum + claimDocumentPageCount(document), 0)
    : 0
}

export function takeClaimDocumentsWithinPageLimit(documents, limit = CLAIM_MAX_DOCUMENT_PAGE_COUNT) {
  const out = []
  let pages = 0
  for (const document of Array.isArray(documents) ? documents : []) {
    const pageCount = claimDocumentPageCount(document)
    if (pages + pageCount > limit) break
    out.push(document)
    pages += pageCount
  }
  return out
}

export async function readClaimFilePageCount(file) {
  if (!isPdfFile(file)) return 1
  let loadingTask = null
  let pdf = null
  try {
    const arrayBuffer = await file.arrayBuffer()
    const bytes = arrayBuffer instanceof ArrayBuffer ? arrayBuffer.slice(0) : arrayBuffer
    const loaded = await getDocumentWithWorkerFallback({ data: bytes })
    loadingTask = loaded.loadingTask
    pdf = loaded.pdf
    return Math.max(Number(pdf?.numPages || 0), 1)
  } finally {
    try { pdf?.destroy?.() } catch { /* ignore */ }
    try { loadingTask?.destroy?.() } catch { /* ignore */ }
  }
}

export async function prepareClaimDocumentFiles(
  fileList,
  {
    initialPageCount = 0,
    maxPageCount = CLAIM_MAX_DOCUMENT_PAGE_COUNT,
  } = {},
) {
  const files = Array.from(fileList || []).filter(Boolean)
  const accepted = []
  let currentPageCount = initialPageCount
  let skippedByPageLimit = 0

  for (const file of files) {
    const pageCount = await readClaimFilePageCount(file)
    if (currentPageCount + pageCount > maxPageCount) {
      skippedByPageLimit += 1
      continue
    }
    accepted.push({ file, pageCount })
    currentPageCount += pageCount
  }

  return {
    accepted,
    skippedByPageLimit,
    totalPicked: files.length,
    totalAcceptedPages: accepted.reduce((sum, item) => sum + item.pageCount, 0),
  }
}
